import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard, Building2, DollarSign, Sparkles, LogOut, Loader2,
  HardDrive, Users, Cpu, CalendarClock, Send, ChevronDown, ChevronRight, Shield,
  Receipt, LifeBuoy, PauseCircle, PlayCircle, RotateCcw, Search, Archive, X,
  Link2, Copy, Check, ExternalLink, Gift, Zap, TrendingDown, History, Eye, FileText, ScrollText, Save,
} from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { RichTextEditor } from "../components/RichTextEditor";
import { LegalContent } from "../lib/legalRender";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useAutoGrow } from "../lib/autogrow";
import logoMark from "../assets/logo-mark.png";

const fmtBytes = (b) => {
  b = Number(b || 0);
  if (b < 1024) return `${b} B`;
  if (b < 1024 ** 2) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 ** 3) return `${(b / 1024 ** 2).toFixed(1)} MB`;
  return `${(b / 1024 ** 3).toFixed(2)} GB`;
};

const NAV = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "tenants", label: "Tenants", icon: Building2 },
  { key: "revenue", label: "Revenue", icon: Receipt },
  { key: "pricing", label: "Pricing", icon: DollarSign },
  { key: "promotions", label: "Promotions", icon: Zap },
  { key: "superai", label: "Super AI", icon: Sparkles },
  { key: "support", label: "Support", icon: LifeBuoy },
  { key: "beta", label: "Beta Access", icon: Users },
  { key: "legal", label: "Legal", icon: ScrollText },
];

export default function SuperAdmin() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const res = await api.get("/superadmin/overview"); setData(res.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div style={{ minHeight: "100vh", background: "#0B0D14", color: "#E7E4DB" }} data-testid="superadmin-root">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 md:px-8 pb-4 border-b" style={{ borderColor: "#1E2233", background: "#0E1119", paddingTop: "calc(env(safe-area-inset-top) + 1rem)" }}>
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg grid place-items-center" style={{ background: "#7C83FF22" }}>
            <img src={logoMark} alt="The Family Vault" className="h-6 w-6 object-contain" />
          </div>
          <div>
            <div className="font-semibold text-sm md:text-base">The Family Vault · Super Admin</div>
            <div className="text-[11px]" style={{ color: "#6B7192" }}>{user?.email}</div>
          </div>
        </div>
        <button onClick={logout} data-testid="superadmin-logout" className="flex items-center gap-2 text-xs rounded-lg px-3 py-2" style={{ background: "#1A1E2E" }}>
          <LogOut size={14} /> Log out
        </button>
      </div>

      <div className="md:flex">
        {/* Sidebar (desktop) / top tabs (mobile) */}
        <div className="md:w-56 md:min-h-[calc(100vh-65px)] border-b md:border-b-0 md:border-r px-3 py-3 md:py-6 flex md:flex-col gap-1 overflow-x-auto" style={{ borderColor: "#1E2233", background: "#0E1119" }}>
          {NAV.map((n) => (
            <button key={n.key} onClick={() => setTab(n.key)} data-testid={`superadmin-nav-${n.key}`}
              className="flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm whitespace-nowrap transition-colors"
              style={tab === n.key ? { background: "#7C83FF22", color: "#C5C9FF" } : { color: "#8890B0" }}>
              <n.icon size={16} /> {n.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 p-4 md:p-8 max-w-6xl">
          {loading ? (
            <div className="grid place-items-center py-32"><Loader2 className="animate-spin" style={{ color: "#7C83FF" }} /></div>
          ) : !data ? null : (
            <>
              {tab === "overview" && <Overview data={data} />}
              {tab === "tenants" && <Tenants tenants={data.tenants} reload={load} />}
              {tab === "revenue" && <Revenue />}
              {tab === "pricing" && <Pricing pricing={data.pricing} packs={data.credit_packs} onSaved={load} />}
              {tab === "promotions" && <Promotions />}
              {tab === "superai" && <SuperAI tenants={data.tenants} />}
              {tab === "support" && <Support />}
              {tab === "beta" && <BetaAccess />}
              {tab === "legal" && <Legal />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function BetaAccess() {
  const [enabled, setEnabled] = useState(false);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        api.get("/superadmin/signup-approval"),
        api.get("/superadmin/pending-signups"),
      ]);
      setEnabled(s.data.enabled);
      setPending(p.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async () => {
    const next = !enabled;
    setEnabled(next);
    try { await api.post("/superadmin/signup-approval", { enabled: next }); toast.success(next ? "Approval gate is ON — new signups need your approval." : "Approval gate is OFF — signups are open."); }
    catch (e) { setEnabled(!next); toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const act = async (id, verb) => {
    setBusy(true);
    try { await api.post(`/superadmin/pending-signups/${id}/${verb}`); setPending((p) => p.filter((u) => u.id !== id)); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const approveAll = async () => {
    setBusy(true);
    try { const r = await api.post("/superadmin/pending-signups/approve-all"); toast.success(`Approved ${r.data.approved} ${r.data.approved === 1 ? "person" : "people"}.`); setPending([]); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="grid place-items-center py-20"><Loader2 className="animate-spin" style={{ color: "#7C83FF" }} /></div>;

  return (
    <div data-testid="beta-access" style={{ color: "#C7CBDA" }}>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: "#E7E9F2", marginBottom: 4 }}>Beta Access</h2>
      <p style={{ fontSize: 13, color: "#8890B0", marginBottom: 20 }}>Control who can join during your private beta.</p>

      <div style={{ borderRadius: 14, border: "1px solid #1E2233", background: "#12151F", padding: 18, marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: "#E7E9F2" }}>Require approval for new signups</div>
            <div style={{ fontSize: 12.5, color: "#8890B0", marginTop: 3, maxWidth: 460 }}>
              When on, anyone who registers is held for your approval and can't sign in until you approve them. Turn off to open registration to everyone instantly.
            </div>
          </div>
          <button onClick={toggle} data-testid="beta-toggle" role="switch" aria-checked={enabled}
            style={{ width: 52, height: 30, borderRadius: 999, border: "none", cursor: "pointer", flexShrink: 0,
              background: enabled ? "#7C83FF" : "#2A2F42", position: "relative", transition: "background .2s" }}>
            <span style={{ position: "absolute", top: 3, left: enabled ? 25 : 3, width: 24, height: 24, borderRadius: "50%", background: "#fff", transition: "left .2s" }} />
          </button>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#E7E9F2" }}>
          Pending signups {pending.length > 0 && <span style={{ color: "#8890B0", fontWeight: 400 }}>({pending.length})</span>}
        </div>
        {pending.length > 0 && (
          <button onClick={approveAll} disabled={busy} data-testid="beta-approve-all"
            style={{ fontSize: 12.5, fontWeight: 600, color: "#C5C9FF", background: "#7C83FF22", border: "none", borderRadius: 8, padding: "8px 12px", cursor: "pointer" }}>
            Approve all
          </button>
        )}
      </div>

      {pending.length === 0 ? (
        <div style={{ fontSize: 13, color: "#8890B0", padding: "28px 0", textAlign: "center", border: "1px dashed #1E2233", borderRadius: 12 }}>
          No one is waiting for approval right now.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {pending.map((u) => (
            <div key={u.id} data-testid={`beta-pending-${u.id}`}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, borderRadius: 12, border: "1px solid #1E2233", background: "#12151F", padding: "12px 14px" }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#E7E9F2", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{u.name || "—"}</div>
                <div style={{ fontSize: 12.5, color: "#8890B0", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{u.email}</div>
              </div>
              <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                <button onClick={() => act(u.id, "reject")} disabled={busy} data-testid={`beta-reject-${u.id}`}
                  style={{ fontSize: 12.5, color: "#C7CBDA", background: "#2A2F42", border: "none", borderRadius: 8, padding: "8px 12px", cursor: "pointer" }}>Reject</button>
                <button onClick={() => act(u.id, "approve")} disabled={busy} data-testid={`beta-approve-${u.id}`}
                  style={{ fontSize: 12.5, fontWeight: 600, color: "#fff", background: "#7C83FF", border: "none", borderRadius: 8, padding: "8px 12px", cursor: "pointer" }}>Approve</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Legal() {
  const [tab, setTab] = useState("privacy");
  const [privacy, setPrivacy] = useState("");
  const [terms, setTerms] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [preview, setPreview] = useState(false);
  const [history, setHistory] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const ref = useRef(null);

  const field = tab === "privacy" ? "privacy_policy" : "terms_of_use";
  const value = tab === "privacy" ? privacy : terms;
  const setValue = tab === "privacy" ? setPrivacy : setTerms;

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/superadmin/legal/history");
      setHistory(data || []);
    } catch (e) { setHistory([]); }
  };

  const restore = (snap) => {
    if (!window.confirm("Load this saved version into the editor? Your current unsaved text will be replaced. You'll still need to click Save & publish.")) return;
    setPrivacy(snap.privacy_policy || "");
    setTerms(snap.terms_of_use || "");
    setShowHistory(false);
    toast.success("Version loaded into editor — review it, then Save & publish");
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/superadmin/legal");
        setPrivacy(data.privacy_policy || "");
        setTerms(data.terms_of_use || "");
        setUpdatedAt(data.updated_at || null);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      finally { setLoading(false); }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/superadmin/legal", { privacy_policy: privacy, terms_of_use: terms });
      setUpdatedAt(data.updated_at || null);
      if (showHistory) loadHistory();
      toast.success("Legal content saved & published");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const TABS = [
    { key: "privacy", label: "Privacy Policy" },
    { key: "terms", label: "Terms of Use" },
  ];

  return (
    <div data-testid="superadmin-legal">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
        <h2 className="text-xl font-semibold">Legal</h2>
        <div className="flex items-center gap-2">
          <button onClick={() => { const n = !showHistory; setShowHistory(n); if (n && history === null) loadHistory(); }}
            data-testid="legal-history-btn"
            className="flex items-center gap-1.5 text-xs rounded-lg px-3 py-2"
            style={showHistory ? { background: "#7C83FF22", color: "#C5C9FF" } : { background: "#1A1E2E", color: "#9DA4FF" }}>
            <History size={13} /> Version history
          </button>
          <a href="/legal" target="_blank" rel="noopener noreferrer" data-testid="legal-view-public"
            className="flex items-center gap-1.5 text-xs rounded-lg px-3 py-2" style={{ background: "#1A1E2E", color: "#9DA4FF" }}>
            <ExternalLink size={13} /> View public page
          </a>
        </div>
      </div>
      <p className="text-xs mb-4" style={{ color: "#6B7192" }}>
        Type or paste your content, format it, and save — it publishes instantly to the public Privacy Policy &amp; Terms page linked from the landing page. Every save is automatically backed up, so nothing is ever lost.
        {updatedAt && <> · Last saved {new Date(updatedAt).toLocaleString()}</>}
      </p>

      {showHistory && (
        <div className="rounded-xl p-3 mb-4" data-testid="legal-history-panel" style={{ background: "#12151F", border: "1px solid #1E2233" }}>
          <div className="text-[11px] uppercase tracking-wider mb-2" style={{ color: "#6B7192" }}>Backups (most recent first)</div>
          {history === null ? (
            <div className="grid place-items-center py-6"><Loader2 size={16} className="animate-spin" style={{ color: "#7C83FF" }} /></div>
          ) : history.length === 0 ? (
            <div className="text-sm py-4 text-center" style={{ color: "#6B7192" }}>No previous versions yet — backups are created each time you save.</div>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {history.map((h, i) => (
                <div key={i} className="flex items-center justify-between gap-3 rounded-lg px-3 py-2" style={{ background: "#0E1119", border: "1px solid #1E2233" }}>
                  <div className="min-w-0">
                    <div className="text-xs" style={{ color: "#C5C9FF" }}>{h.saved_at ? new Date(h.saved_at).toLocaleString() : "Earlier version"}</div>
                    <div className="text-[11px] truncate" style={{ color: "#6B7192" }}>
                      {h.saved_by ? `by ${h.saved_by} · ` : ""}{(h.privacy_policy || "").replace(/<[^>]+>/g, "").slice(0, 60) || "empty"}…
                    </div>
                  </div>
                  <button onClick={() => restore(h)} data-testid={`legal-history-restore-${i}`}
                    className="flex items-center gap-1.5 text-xs rounded-lg px-2.5 py-1.5 shrink-0" style={{ background: "#7C83FF", color: "#0B0D14" }}>
                    <RotateCcw size={12} /> Load
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-xl mb-4 max-w-sm" style={{ background: "#12151F", border: "1px solid #1E2233" }}>
        {TABS.map((t) => (
          <button key={t.key} onClick={() => { setTab(t.key); setPreview(false); }} data-testid={`legal-admin-tab-${t.key}`}
            className="flex-1 rounded-lg py-2 text-sm font-semibold transition-colors"
            style={tab === t.key ? { background: "#7C83FF22", color: "#C5C9FF" } : { color: "#8890B0" }}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid place-items-center py-16"><Loader2 className="animate-spin" style={{ color: "#7C83FF" }} /></div>
      ) : (
        <div className="rounded-xl p-4" style={{ background: "#12151F", border: "1px solid #1E2233" }}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] uppercase tracking-wider" style={{ color: "#6B7192" }}>
              {tab === "privacy" ? "Privacy Policy" : "Terms of Use"} · {value.length} chars
            </span>
            <button onClick={() => setPreview((p) => !p)} data-testid="legal-admin-preview-toggle"
              className="flex items-center gap-1.5 text-xs rounded-lg px-2.5 py-1.5"
              style={preview ? { background: "#7C83FF22", color: "#C5C9FF" } : { background: "#1A1E2E", color: "#8890B0" }}>
              <Eye size={13} /> {preview ? "Editing" : "Preview"}
            </button>
          </div>

          {preview ? (
            value.trim() ? (
              <div className="vault-md legal-md rounded-lg p-4 max-h-[520px] overflow-y-auto" data-testid="legal-admin-preview"
                style={{ background: "#0E1119", border: "1px solid #1E2233", color: "#B7BDD6" }}>
                <LegalContent content={value} />
              </div>
            ) : (
              <div className="text-sm py-10 text-center" style={{ color: "#6B7192" }}>Nothing to preview yet.</div>
            )
          ) : (
            <>
              <p className="text-[11px] mb-2" style={{ color: "#6B7192" }}>
                Select text, then use <b>B</b> / <i>I</i> / <u>U</u> or the size menu to format it.
              </p>
              <RichTextEditor
                value={value}
                onChange={setValue}
                testid={`legal-admin-input-${tab}`}
                placeholder={`Type or paste the ${tab === "privacy" ? "privacy policy" : "terms of use"} here…`}
              />
            </>
          )}

          <button onClick={save} disabled={saving} data-testid="legal-admin-save"
            className="mt-4 flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold"
            style={{ background: "#7C83FF", color: "#0B0D14", opacity: saving ? 0.6 : 1 }}>
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save &amp; publish
          </button>
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-xl p-4" style={{ background: "#12151F", border: "1px solid #1E2233" }} data-testid={`stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider" style={{ color: "#6B7192" }}>
        <Icon size={14} style={{ color: "#7C83FF" }} /> {label}
      </div>
      <div className="text-2xl font-bold mt-2">{value}</div>
      {sub && <div className="text-[11px] mt-1" style={{ color: "#6B7192" }}>{sub}</div>}
    </div>
  );
}

function Overview({ data }) {
  const t = data.totals;
  const pct = t.storage_limit ? Math.round((t.storage_used / t.storage_limit) * 100) : 0;
  return (
    <div data-testid="superadmin-overview">
      <h2 className="text-xl font-semibold mb-4">Platform Overview</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Stat icon={Building2} label="Tenants" value={t.tenants} />
        <Stat icon={Users} label="Users" value={t.users} />
        <Stat icon={HardDrive} label="Storage Used" value={fmtBytes(t.storage_used)} sub={`of ${fmtBytes(t.storage_limit)} pool · ${pct}%`} />
        <Stat icon={Cpu} label="AI Credits" value={t.ai_credits} sub="consumed across tenants" />
        <Stat icon={CalendarClock} label="Reunions" value={t.reunions} />
        <Stat icon={DollarSign} label="Reunion Revenue" value={`$${(t.reunion_revenue || 0).toFixed(2)}`} />
      </div>
    </div>
  );
}

function PreviewLink({ code, tenantId }) {
  const [copied, setCopied] = useState(false);
  const link = `${window.location.origin}/join/${code}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); }
    catch { const el = document.createElement("textarea"); el.value = link; document.body.appendChild(el); el.select(); document.execCommand("copy"); document.body.removeChild(el); }
    setCopied(true); toast.success("Preview link copied");
    setTimeout(() => setCopied(false), 1600);
  };
  return (
    <div data-testid={`tenant-preview-${tenantId}`}>
      <div className="text-[11px] uppercase tracking-wider mb-2 flex items-center gap-1.5" style={{ color: "#6B7192" }}>
        <Link2 size={13} style={{ color: "#7C83FF" }} /> Family page preview link
      </div>
      <div className="flex items-center gap-2 rounded-lg px-3 py-2" style={{ background: "#0E1119", border: "1px solid #1E2233" }}>
        <span className="flex-1 text-xs truncate font-mono" data-testid={`tenant-preview-link-${tenantId}`} style={{ color: "#C5C9FF" }}>{link}</span>
        <button onClick={copy} data-testid={`tenant-preview-copy-${tenantId}`} title="Copy link"
          className="shrink-0 grid place-items-center h-7 w-7 rounded-md" style={{ background: "#12151F", color: copied ? "#7FBF98" : "#8890B0" }}>
          {copied ? <Check size={14} /> : <Copy size={14} />}
        </button>
        <a href={link} target="_blank" rel="noopener noreferrer" data-testid={`tenant-preview-open-${tenantId}`} title="Open in new tab"
          className="shrink-0 grid place-items-center h-7 w-7 rounded-md" style={{ background: "#12151F", color: "#8890B0" }}>
          <ExternalLink size={14} />
        </a>
      </div>
      <div className="text-[10px] mt-1.5" style={{ color: "#6B7192" }}>Opens this family's join page — no family name in the URL.</div>
    </div>
  );
}

function MemberAdminRow({ m, pause, reload }) {
  const [panel, setPanel] = useState(null); // "comp" | "credits" | null
  const [tier, setTier] = useState("branches");
  const [months, setMonths] = useState(3);
  const [busy, setBusy] = useState(false);
  // credits panel state
  const [detail, setDetail] = useState(null); // {credits, usage, cost_rate, pl, free_credits}
  const [amount, setAmount] = useState(100);
  const [showFree, setShowFree] = useState(false);
  const [duration, setDuration] = useState("never");
  const [showActivity, setShowActivity] = useState(false);

  const grant = async () => {
    setBusy(true);
    try {
      await api.post(`/superadmin/users/${m.user_id}/comp`, { tier, months });
      toast.success(`Granted ${months} mo of ${tier} to ${m.name}`);
      setPanel(null); reload && reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const revoke = async () => {
    setBusy(true);
    try {
      await api.delete(`/superadmin/users/${m.user_id}/comp`);
      toast.success("Free access revoked"); reload && reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openCredits = async () => {
    if (panel === "credits") { setPanel(null); return; }
    setPanel("credits");
    try {
      const { data } = await api.get(`/superadmin/users/${m.user_id}/ai-usage`);
      setDetail(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const grantCredits = async () => {
    const amt = parseInt(amount, 10);
    if (!amt || amt <= 0) { toast.error("Enter a positive number of credits"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/superadmin/users/${m.user_id}/credits`, { amount: amt, duration });
      const exp = data.expires_at ? `expires ${data.expires_at.slice(0, 10)}` : "never expires";
      toast.success(`Added ${amt.toLocaleString()} credits to ${m.name} · ${exp} (est. cost $${data.estimated_cost})`);
      setPanel(null); reload && reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const rate = detail?.cost_rate?.rate ?? 0.015;
  const estCost = ((parseInt(amount, 10) || 0) * rate).toFixed(2);
  const c = m.credits || {};

  return (
    <div className="rounded-lg" style={{ background: "#0E1119" }} data-testid={`member-row-${m.user_id}`}>
      <div className="flex items-center justify-between px-3 py-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium">{m.name}</span>
            <span className="text-[11px]" style={{ color: "#6B7192" }}>· {m.role}</span>
            {m.is_test && <span className="text-[9px] uppercase rounded px-1 py-0.5" style={{ background: "#C9A22722", color: "#C9A227" }} title="Demo / testing account">test</span>}
            {m.paused && <span className="text-[9px] uppercase rounded px-1 py-0.5" style={{ background: "#E0685E22", color: "#E0685E" }}>paused</span>}
            {m.comp && (
              <span className="text-[9px] uppercase rounded px-1.5 py-0.5" style={{ background: "#7C83FF22", color: "#A6ABFF" }}
                data-testid={`member-comp-${m.user_id}`}>
                free {m.comp.tier} · til {m.comp.expires_at.slice(0, 10)}
              </span>
            )}
          </div>
          <button onClick={() => setShowActivity(true)} data-testid={`user-activity-btn-${m.user_id}`}
            className="mt-1 inline-flex items-center gap-1 text-[10px] rounded px-1.5 py-0.5"
            style={{ background: "#12151F", color: "#8FA0FF", border: "1px solid #1E2233" }}>
            <History size={10} /> See activity
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={openCredits} data-testid={`user-credits-toggle-${m.user_id}`}
            title="AI credits & usage"
            className="flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px]"
            style={{ background: panel === "credits" ? "#7C83FF22" : "#12151F", color: "#C9A227" }}>
            <Zap size={12} />
            <span data-testid={`member-credits-${m.user_id}`} style={{ color: "#C5C9FF" }}>
              {(c.total_available ?? 0).toLocaleString()}
            </span>
          </button>
          <PlanBadge plan={m.plan} status={m.sub_status} />
          <button onClick={() => setPanel(panel === "comp" ? null : "comp")} data-testid={`user-comp-toggle-${m.user_id}`}
            title="Grant free access" style={{ color: panel === "comp" ? "#A6ABFF" : "#8890B0" }}>
            <Gift size={15} />
          </button>
          <button onClick={() => pause({ user_id: m.user_id }, !m.paused)} data-testid={`user-pause-${m.user_id}`}
            title={m.paused ? "Unpause user" : "Pause user"} style={{ color: m.paused ? "#7FBF98" : "#E0685E" }}>
            {m.paused ? <PlayCircle size={15} /> : <PauseCircle size={15} />}
          </button>
        </div>
      </div>

      {panel === "credits" && (
        <div className="px-3 pb-3 pt-2 border-t space-y-3" style={{ borderColor: "#1E2233" }} data-testid={`credits-panel-${m.user_id}`}>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-lg py-2" style={{ background: "#12151F" }}>
              <div className="text-[10px]" style={{ color: "#6B7192" }}>Available</div>
              <div className="text-sm font-semibold" style={{ color: "#C5C9FF" }}>{(c.total_available ?? 0).toLocaleString()}</div>
            </div>
            <div className="rounded-lg py-2" style={{ background: "#12151F" }}>
              <div className="text-[10px]" style={{ color: "#6B7192" }}>Monthly left</div>
              <div className="text-sm font-semibold">{(c.monthly_remaining ?? 0)}/{(c.monthly_cap ?? 0)}</div>
            </div>
            <div className="rounded-lg py-2" style={{ background: "#12151F" }}>
              <div className="text-[10px]" style={{ color: "#6B7192" }}>Purchased</div>
              <div className="text-sm font-semibold" style={{ color: "#C9A227" }}>{(c.purchased_balance ?? 0).toLocaleString()}</div>
            </div>
          </div>

          {(() => {
            const pl = detail?.pl || m.pl || {};
            const net = pl.net ?? 0;
            const freeCount = pl.free_count ?? 0;
            const freeCost = pl.free_cost ?? 0;
            return (
              <div className="rounded-lg p-2.5" style={{ background: "#12151F", border: "1px solid #1E2233" }} data-testid={`user-pl-${m.user_id}`}>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <div className="text-[10px]" style={{ color: "#6B7192" }}>Revenue gained</div>
                    <div className="text-sm font-semibold" style={{ color: "#7FBF98" }}>${(pl.revenue ?? 0).toFixed(2)}</div>
                  </div>
                  <div>
                    <div className="text-[10px]" style={{ color: "#6B7192" }}>Cost used</div>
                    <div className="text-sm font-semibold" style={{ color: "#E0685E" }}>${(pl.ai_cost ?? 0).toFixed(2)}</div>
                  </div>
                  <div>
                    <div className="text-[10px]" style={{ color: "#6B7192" }}>Net</div>
                    <div className="text-sm font-semibold" data-testid={`user-net-${m.user_id}`} style={{ color: net >= 0 ? "#7FBF98" : "#E0685E" }}>
                      {net < 0 ? "−" : ""}${Math.abs(net).toFixed(2)}
                    </div>
                  </div>
                </div>
                {freeCount > 0 && (
                  <div className="mt-2 flex items-center justify-between gap-2 rounded-lg px-2 py-1.5" style={{ background: "#C9A2270F", border: "1px solid #C9A22733" }} data-testid={`user-free-flag-${m.user_id}`}>
                    <span className="text-[10px] flex items-center gap-1" style={{ color: "#C9A227" }}>
                      <TrendingDown size={11} /> Cost elevated by {freeCount.toLocaleString()} free credits issued (~${freeCost.toFixed(2)})
                    </span>
                    <button onClick={() => setShowFree((s) => !s)} data-testid={`user-freecredits-toggle-${m.user_id}`}
                      className="text-[10px] rounded px-2 py-1 shrink-0" style={{ background: "#C9A22722", color: "#C9A227" }}>
                      {showFree ? "Hide" : "View free credits"}
                    </button>
                  </div>
                )}
                {freeCount > 0 && showFree && (
                  <div className="mt-2 space-y-1" data-testid={`user-freecredits-list-${m.user_id}`}>
                    {(detail?.free_credits?.awards || []).map((a, i) => (
                      <div key={i} className="flex items-center justify-between text-[10px] rounded px-2 py-1" style={{ background: "#0E1119" }}>
                        <span style={{ color: "#C9A227" }}>+{a.amount.toLocaleString()} credits</span>
                        <span style={{ color: "#6B7192" }}>
                          {(a.created_at || "").slice(0, 16).replace("T", " ")} · ~${a.cost.toFixed(2)} · {a.expires_at ? `exp ${a.expires_at.slice(0, 10)}` : "no expiry"}
                        </span>
                      </div>
                    ))}
                    {(!detail || (detail.free_credits?.awards || []).length === 0) && <div className="text-[10px]" style={{ color: "#6B7192" }}>Loading…</div>}
                  </div>
                )}
              </div>
            );
          })()}

          {!detail ? (
            <div className="text-[11px] flex items-center gap-2" style={{ color: "#6B7192" }}><Loader2 size={12} className="animate-spin" /> Loading usage…</div>
          ) : (
            <>
              <div>
                <div className="text-[10px] uppercase tracking-wider mb-1 flex items-center gap-1" style={{ color: "#6B7192" }}>
                  <History size={11} /> Spent {detail.usage.total} credits total (${detail.usage.total_cost.toFixed(2)}) · what on
                </div>
                {Object.keys(detail.usage.by_tool).length === 0 ? (
                  <div className="text-[11px]" style={{ color: "#6B7192" }}>No AI usage yet</div>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(detail.usage.by_tool).sort((a, b) => b[1] - a[1]).map(([t, v]) => (
                      <span key={t} className="text-[10px] rounded-full px-2 py-0.5" style={{ background: "#12151F", color: "#9DA4FF" }}>
                        {TOOL_LABEL[t] || t}: {v} cr · ${Number(detail.usage.by_tool_cost[t] || 0).toFixed(2)}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {Object.keys(detail.usage.monthly).length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: "#6B7192" }}>Monthly consumption</div>
                  <MonthBreakdown data={detail.usage.monthly} fmt={(v) => `${v} cr`} />
                </div>
              )}

              {detail.usage.recent.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: "#6B7192" }}>Recent activity</div>
                  <div className="max-h-28 overflow-y-auto no-scrollbar space-y-1">
                    {detail.usage.recent.map((r, i) => (
                      <div key={i} className="flex items-center justify-between text-[10px] rounded px-2 py-1" style={{ background: "#12151F" }}>
                        <span style={{ color: "#9DA4FF" }}>{TOOL_LABEL[r.tool] || r.tool}</span>
                        <span style={{ color: "#6B7192" }}>{(r.created_at || "").slice(0, 16).replace("T", " ")} · {r.credits} cr · ${Number(r.cost).toFixed(3)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Grant free credits */}
              <div className="rounded-lg p-2.5" style={{ background: "#12151F", border: "1px solid #1E2233" }}>
                <div className="text-[10px] uppercase tracking-wider mb-2" style={{ color: "#6B7192" }}>Issue free credits</div>
                <div className="flex items-center gap-2">
                  <input type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)}
                    data-testid={`credits-amount-${m.user_id}`}
                    className="w-24 rounded-lg px-2.5 py-1.5 text-xs outline-none"
                    style={{ background: "#0E1119", border: "1px solid #1E2233", color: "#E7EAF5" }} />
                  <span className="text-[11px]" style={{ color: "#6B7192" }}>credits</span>
                  <button onClick={grantCredits} disabled={busy} data-testid={`credits-grant-${m.user_id}`}
                    className="ml-auto text-xs rounded-lg px-4 py-1.5 font-semibold" style={{ background: "#5B8C6E", color: "#fff" }}>
                    {busy ? "…" : "Grant"}
                  </button>
                </div>
                <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                  <span className="text-[10px]" style={{ color: "#6B7192" }}>Expires in</span>
                  {[["30d", "30 days"], ["60d", "60 days"], ["90d", "90 days"], ["1y", "1 year"], ["never", "Never"]].map(([val, label]) => (
                    <button key={val} onClick={() => setDuration(val)} data-testid={`credits-duration-${val}-${m.user_id}`}
                      className="text-[10px] rounded-full px-2.5 py-1"
                      style={duration === val ? { background: "#7C83FF", color: "#fff" } : { background: "#0E1119", color: "#8890B0", border: "1px solid #1E2233" }}>
                      {label}
                    </button>
                  ))}
                </div>
                <div className="flex items-center justify-between mt-2 text-[11px]">
                  <span style={{ color: "#6B7192" }}>
                    Est. cost to us · <span style={{ color: detail.cost_rate.basis === "personalized" ? "#7FBF98" : "#8890B0" }}>{detail.cost_rate.basis}</span> rate ${rate.toFixed(3)}/cr
                  </span>
                  <span className="font-semibold" data-testid={`credits-estcost-${m.user_id}`} style={{ color: "#E0685E" }}>−${estCost}</span>
                </div>
                <p className="text-[9px] mt-1" style={{ color: "#6B7192" }}>
                  {detail.cost_rate.basis === "blended"
                    ? `Blended average until this user has spent 100 credits (spent ${detail.cost_rate.total_spent}).`
                    : `Personalized to this user's own spending mix.`} Recorded as a "free credits" loss in Revenue; the loss shrinks automatically as unused credits expire.
                </p>
              </div>
            </>
          )}
        </div>
      )}

      {panel === "comp" && (
        <div className="px-3 pb-3 pt-1 border-t" style={{ borderColor: "#1E2233" }} data-testid={`comp-panel-${m.user_id}`}>
          {m.comp ? (
            <div className="flex items-center justify-between gap-2 mt-2">
              <span className="text-[11px]" style={{ color: "#8890B0" }}>Active free {m.comp.tier} until {m.comp.expires_at.slice(0, 10)}. Re-authorize after it expires.</span>
              <button onClick={revoke} disabled={busy} data-testid={`comp-revoke-${m.user_id}`}
                className="text-xs rounded-lg px-3 py-1.5 shrink-0" style={{ background: "#E0685E22", color: "#E0685E" }}>Revoke</button>
            </div>
          ) : (
            <div className="mt-2 space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-[11px] w-12" style={{ color: "#6B7192" }}>Plan</span>
                {["branches", "legacy"].map((tt) => (
                  <button key={tt} onClick={() => setTier(tt)} data-testid={`comp-tier-${tt}-${m.user_id}`}
                    className="text-xs rounded-lg px-3 py-1.5 capitalize"
                    style={tier === tt ? { background: "#7C83FF", color: "#fff" } : { background: "#12151F", color: "#8890B0" }}>{tt}</button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] w-12" style={{ color: "#6B7192" }}>Months</span>
                {[1, 2, 3].map((mm) => (
                  <button key={mm} onClick={() => setMonths(mm)} data-testid={`comp-months-${mm}-${m.user_id}`}
                    className="text-xs rounded-lg px-3 py-1.5"
                    style={months === mm ? { background: "#7C83FF", color: "#fff" } : { background: "#12151F", color: "#8890B0" }}>{mm}</button>
                ))}
                <button onClick={grant} disabled={busy} data-testid={`comp-grant-${m.user_id}`}
                  className="ml-auto text-xs rounded-lg px-4 py-1.5 font-semibold" style={{ background: "#5B8C6E", color: "#fff" }}>
                  {busy ? "…" : "Grant free access"}
                </button>
              </div>
              <p className="text-[10px]" style={{ color: "#6B7192" }}>Max 3 months per authorization. You can re-authorize after it expires.</p>
            </div>
          )}
        </div>
      )}
      {showActivity && <ActivitySheet userId={m.user_id} name={m.name} onClose={() => setShowActivity(false)} />}
    </div>
  );
}

function fmtDur(s) {
  s = Math.round(s || 0);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
}

function ActivitySheet({ userId, name, onClose }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("sessions");
  const [openThread, setOpenThread] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/superadmin/users/${userId}/activity`); setData(data); }
      catch (e) { toast.error(formatApiError(e.response?.data?.detail)); onClose(); }
    })();
  }, [userId]); // eslint-disable-line
  const exportJson = () => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `activity-${name}-${new Date().toISOString().slice(0, 10)}.json`;
    a.click(); URL.revokeObjectURL(url);
  };
  const s = data?.summary || {};
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-3" style={{ background: "rgba(6,8,14,0.72)" }}
      data-testid={`activity-modal-${userId}`} onClick={onClose}>
      <div className="w-full max-w-3xl rounded-2xl overflow-hidden flex flex-col" style={{ background: "#0B0E16", border: "1px solid #1E2233", maxHeight: "90vh" }}
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "#1E2233" }}>
          <div className="flex items-center gap-2"><History size={16} className="gold-text" /><span className="font-semibold">Activity · {name}</span></div>
          <div className="flex items-center gap-2">
            <button onClick={exportJson} disabled={!data} data-testid={`activity-export-${userId}`}
              className="text-[11px] rounded-lg px-3 py-1.5 flex items-center gap-1" style={{ background: "#7C83FF", color: "#fff" }}>Export</button>
            <button onClick={onClose} data-testid={`activity-close-${userId}`} style={{ color: "#8890B0" }}><X size={18} /></button>
          </div>
        </div>
        {!data ? (
          <div className="grid place-items-center py-16"><Loader2 className="animate-spin" style={{ color: "#7C83FF" }} /></div>
        ) : (
          <>
            <div className="grid grid-cols-4 gap-2 px-4 py-3 text-center border-b" style={{ borderColor: "#1E2233" }}>
              {[["Sessions", s.total_sessions], ["Time", fmtDur(s.total_seconds)], ["Logins", s.total_logins], ["Failed", s.failed_logins]].map(([l, v]) => (
                <div key={l}><div className="text-[10px]" style={{ color: "#6B7192" }}>{l}</div><div className="text-sm font-semibold">{v}</div></div>
              ))}
            </div>
            <div className="flex gap-1 px-4 pt-3">
              {[["sessions", "Sessions"], ["logins", "Login attempts"], ["chats", "AI chats"]].map(([k, l]) => (
                <button key={k} onClick={() => setTab(k)} data-testid={`activity-tab-${k}-${userId}`}
                  className="text-[11px] rounded-t-lg px-3 py-1.5"
                  style={tab === k ? { background: "#12151F", color: "#E7EAF5" } : { color: "#8890B0" }}>{l}</button>
              ))}
            </div>
            <div className="flex-1 overflow-y-auto no-scrollbar px-4 py-3 space-y-2" style={{ background: "#12151F" }}>
              {tab === "logins" && data.login_attempts.map((l, i) => (
                <div key={i} className="flex items-center justify-between text-[11px] rounded-lg px-3 py-2" style={{ background: "#0E1119" }}>
                  <span className="flex items-center gap-2">
                    <span style={{ color: l.success ? "#7FBF98" : "#E0685E" }}>{l.success ? "✓ success" : "✕ failed"}</span>
                    <span style={{ color: "#8890B0" }}>{l.ip} · {l.location}</span>
                  </span>
                  <span style={{ color: "#6B7192" }}>{(l.created_at || "").slice(0, 16).replace("T", " ")}</span>
                </div>
              ))}
              {tab === "logins" && data.login_attempts.length === 0 && <Empty />}

              {tab === "sessions" && data.sessions.map((ss) => (
                <div key={ss.id} className="rounded-lg p-3" style={{ background: "#0E1119" }} data-testid={`activity-session-${ss.id}`}>
                  <div className="flex items-center justify-between text-[11px] mb-2">
                    <span style={{ color: "#C5C9FF" }}>{(ss.started_at || "").slice(0, 16).replace("T", " ")} · {fmtDur(ss.duration_s)}</span>
                    <span style={{ color: "#6B7192" }}>{ss.ip} · {ss.location}</span>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <div className="text-[9px] uppercase tracking-wider mb-1" style={{ color: "#6B7192" }}>Pages · time ({ss.page_views} views)</div>
                      {Object.entries(ss.page_time).sort((a, b) => b[1] - a[1]).map(([p, t]) => (
                        <div key={p} className="flex justify-between text-[10px]"><span style={{ color: "#9DA4FF" }}>{p}</span><span style={{ color: "#6B7192" }}>{fmtDur(t)}</span></div>
                      ))}
                    </div>
                    <div>
                      <div className="text-[9px] uppercase tracking-wider mb-1" style={{ color: "#6B7192" }}>Buttons clicked ({ss.click_total})</div>
                      <div className="max-h-24 overflow-y-auto no-scrollbar">
                        {Object.entries(ss.click_counts).sort((a, b) => b[1] - a[1]).map(([c, n]) => (
                          <div key={c} className="flex justify-between text-[10px]"><span className="truncate mr-2" style={{ color: "#C9A227" }}>{c}</span><span style={{ color: "#6B7192" }}>×{n}</span></div>
                        ))}
                      </div>
                    </div>
                  </div>
                  {Object.keys(ss.vault_time).length > 0 && (
                    <div className="mt-2">
                      <div className="text-[9px] uppercase tracking-wider mb-1" style={{ color: "#6B7192" }}>Vaults · time & actions</div>
                      {Object.entries(ss.vault_time).map(([v, t]) => (
                        <div key={v} className="text-[10px] mb-1">
                          <div className="flex justify-between"><span style={{ color: "#9DA4FF" }}>{TOOL_LABEL[v] || v}</span><span style={{ color: "#6B7192" }}>{fmtDur(t)}</span></div>
                          {(ss.vault_actions[v] || []).length > 0 && (
                            <div className="pl-2" style={{ color: "#6B7192" }}>{(ss.vault_actions[v] || []).map((a, i) => `${a.action}`).join(" · ")}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {tab === "sessions" && data.sessions.length === 0 && <Empty />}

              {tab === "chats" && data.ai_threads.map((th, i) => (
                <div key={i} className="rounded-lg" style={{ background: "#0E1119" }}>
                  <button onClick={() => setOpenThread(openThread === i ? null : i)} data-testid={`activity-thread-${i}-${userId}`}
                    className="w-full flex items-center justify-between px-3 py-2 text-[11px]">
                    <span style={{ color: "#C5C9FF" }}>{TOOL_LABEL[th.tool] || th.tool}</span>
                    <span style={{ color: "#6B7192" }}>{th.day} · {th.turns.length} msgs</span>
                  </button>
                  {openThread === i && (
                    <div className="px-3 pb-3 space-y-2">
                      {th.turns.map((t, j) => (
                        <div key={j} className="text-[11px] rounded-lg px-2.5 py-1.5"
                          style={{ background: t.role === "user" ? "#1A1E2E" : "#12151F", color: t.role === "user" ? "#E7EAF5" : "#B9C0E8" }}>
                          <span className="text-[9px] uppercase tracking-wider mr-1" style={{ color: t.role === "user" ? "#8FA0FF" : "#C9A227" }}>{t.role === "user" ? "User" : "AI"}</span>
                          {t.text}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {tab === "chats" && data.ai_threads.length === 0 && <Empty />}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Empty() {
  return <div className="text-center py-10 text-[11px]" style={{ color: "#6B7192" }}>Nothing recorded yet.</div>;
}

function SortBar({ value, onChange, testid, label }) {
  const opts = [["default", "Default"], ["losses", "Greatest losses"], ["profits", "Greatest profits"]];
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid={testid}>
      {label && <span className="text-[10px] uppercase tracking-wider mr-1" style={{ color: "#6B7192" }}>{label}</span>}
      {opts.map(([v, l]) => (
        <button key={v} onClick={() => onChange(v)} data-testid={`${testid}-${v}`}
          className="text-[11px] rounded-full px-3 py-1"
          style={value === v ? { background: "#7C83FF", color: "#fff" } : { background: "#12151F", color: "#8890B0", border: "1px solid #1E2233" }}>
          {l}
        </button>
      ))}
    </div>
  );
}

function sortByNet(list, mode) {
  if (mode === "default") return list;
  const arr = [...list];
  const net = (x) => (x.pl?.net ?? 0);
  arr.sort((a, b) => (mode === "losses" ? net(a) - net(b) : net(b) - net(a)));
  return arr;
}

function Tenants({ tenants, reload }) {
  const [open, setOpen] = useState(null);
  const [sort, setSort] = useState("default");
  const [memberSort, setMemberSort] = useState("default");
  const pause = async (payload, next) => {
    try { await api.post("/superadmin/pause", { ...payload, paused: next }); toast.success(next ? "Paused" : "Unpaused"); reload && reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const sortedTenants = sortByNet(tenants, sort);
  return (
    <div data-testid="superadmin-tenants">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h2 className="text-xl font-semibold">Family Tenants ({tenants.length})</h2>
        <SortBar value={sort} onChange={setSort} testid="tenant-sort" label="Sort tenants" />
      </div>
      <div className="space-y-2">
        {sortedTenants.map((t) => {
          const pct = t.storage.limit_bytes ? Math.round((t.storage.used_bytes / t.storage.limit_bytes) * 100) : 0;
          const isOpen = open === t.id;
          const tNet = t.pl?.net ?? 0;
          return (
            <div key={t.id} className="rounded-xl overflow-hidden" style={{ background: "#12151F", border: "1px solid #1E2233" }}>
              <button onClick={() => setOpen(isOpen ? null : t.id)} data-testid={`tenant-row-${t.id}`}
                className="w-full flex items-center justify-between px-4 py-3 text-left">
                <div className="flex items-center gap-3 min-w-0">
                  {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  <div className="min-w-0">
                    <div className="font-medium truncate flex items-center gap-2">{t.name}
                      {t.paused && <span className="text-[9px] uppercase rounded px-1.5 py-0.5" style={{ background: "#E0685E22", color: "#E0685E" }}>paused</span>}</div>
                    <div className="text-[11px]" style={{ color: "#6B7192" }}>{t.member_count} members · {t.reunion_count} reunions · {fmtBytes(t.storage.used_bytes)}/{fmtBytes(t.storage.limit_bytes)}</div>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs font-semibold" data-testid={`tenant-net-badge-${t.id}`} style={{ color: tNet >= 0 ? "#7FBF98" : "#E0685E" }}>
                    {tNet < 0 ? "−" : ""}${Math.abs(tNet).toFixed(2)}
                  </div>
                  <div className="text-[10px]" style={{ color: "#8890B0" }}>{t.ai_usage.total} AI</div>
                </div>
              </button>
              {isOpen && (
                <div className="px-4 pb-4 space-y-4 text-sm" data-testid={`tenant-detail-${t.id}`}>
                  <div className="flex justify-end">
                    <button onClick={() => pause({ family_id: t.id }, !t.paused)} data-testid={`tenant-pause-${t.id}`}
                      className="flex items-center gap-1.5 text-xs rounded-lg px-3 py-1.5" style={{ background: t.paused ? "#5B8C6E22" : "#E0685E22", color: t.paused ? "#7FBF98" : "#E0685E" }}>
                      {t.paused ? <PlayCircle size={13} /> : <PauseCircle size={13} />} {t.paused ? "Unpause tenant" : "Pause tenant"}
                    </button>
                  </div>
                  {t.preview_code && <PreviewLink code={t.preview_code} tenantId={t.id} />}
                  {/* Members */}
                  <div>
                    <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                      <div className="text-[11px] uppercase tracking-wider" style={{ color: "#6B7192" }}>Members & plans</div>
                      <SortBar value={memberSort} onChange={setMemberSort} testid={`member-sort-${t.id}`} />
                    </div>
                    <div className="space-y-1">
                      {sortByNet(t.members, memberSort).map((m) => (
                        <MemberAdminRow key={m.user_id} m={m} pause={pause} reload={reload} />
                      ))}
                    </div>
                  </div>
                  {/* Costs & revenue */}
                  <TenantCosts t={t} />
                  {/* Storage */}
                  <div>
                    <div className="text-[11px] uppercase tracking-wider mb-2 flex items-center justify-between" style={{ color: "#6B7192" }}>
                      <span>Storage pool · {pct}% used</span>
                      <span data-testid={`tenant-storage-${t.id}`} style={{ color: "#C5C9FF" }}>{fmtBytes(t.storage.used_bytes)} / {fmtBytes(t.storage.limit_bytes)}</span>
                    </div>
                    <div className="h-2 rounded-full overflow-hidden mb-2" style={{ background: "#0E1119" }}>
                      <div className="h-full" style={{ width: `${Math.min(pct, 100)}%`, background: pct > 90 ? "#E0685E" : "#7C83FF" }} />
                    </div>
                    <MonthBreakdown data={t.storage.monthly} fmt={fmtBytes} />
                  </div>
                  {/* AI usage */}
                  <div>
                    <div className="text-[11px] uppercase tracking-wider mb-2" style={{ color: "#6B7192" }}>AI credit usage · {t.ai_usage.total} total</div>
                    <MonthBreakdown data={t.ai_usage.monthly} fmt={(v) => `${v} cr`} />
                  </div>
                  {/* Reunions */}
                  <div>
                    <div className="text-[11px] uppercase tracking-wider mb-2" style={{ color: "#6B7192" }}>Reunions · ${t.reunion_revenue.toFixed(2)} in fees</div>
                    {t.reunions.length === 0 ? <div className="text-[11px]" style={{ color: "#6B7192" }}>None</div> : (
                      <div className="space-y-1">
                        {t.reunions.map((r) => (
                          <div key={r.id} className="flex items-center justify-between rounded-lg px-3 py-1.5 text-xs" style={{ background: "#0E1119" }}>
                            <span className="truncate">{r.title}</span>
                            <span style={{ color: "#9DA4FF" }}>${r.fee.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PlanBadge({ plan, status }) {
  const colors = { roots: "#5B8C6E", branches: "#7C83FF", legacy: "#C9A227" };
  return (
    <span className="text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 font-semibold"
      style={{ background: `${colors[plan] || "#7C83FF"}22`, color: colors[plan] || "#7C83FF" }}>
      {plan}{status === "pending_payment" ? " · unpaid" : ""}
    </span>
  );
}

function MonthBreakdown({ data, fmt }) {
  const entries = Object.entries(data || {}).sort();
  if (!entries.length) return <div className="text-[11px]" style={{ color: "#6B7192" }}>No activity yet</div>;
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
      {entries.map(([mo, v]) => (
        <div key={mo} className="flex items-center justify-between rounded-lg px-2.5 py-1.5 text-[11px]" style={{ background: "#0E1119" }}>
          <span style={{ color: "#6B7192" }}>{mo}</span><span>{fmt(v)}</span>
        </div>
      ))}
    </div>
  );
}

function TenantCosts({ t }) {
  const [showFree, setShowFree] = useState(false);
  const [free, setFree] = useState(null);
  const pl = t.pl || {};
  const net = pl.net ?? 0;
  const toggleFree = async () => {
    if (showFree) { setShowFree(false); return; }
    setShowFree(true);
    if (!free) {
      try { const { data } = await api.get(`/superadmin/families/${t.id}/free-credits`); setFree(data); }
      catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    }
  };
  return (
    <div data-testid={`tenant-costs-${t.id}`}>
      <div className="text-[11px] uppercase tracking-wider mb-2 flex items-center justify-between" style={{ color: "#6B7192" }}>
        <span>Costs & revenue</span>
        <button onClick={toggleFree} data-testid={`tenant-freecredits-toggle-${t.id}`}
          className="normal-case text-[10px] rounded px-2 py-1 flex items-center gap-1" style={{ background: "#C9A22722", color: "#C9A227" }}>
          <Gift size={11} /> {showFree ? "Hide free credits" : "Free credits"}{pl.free_count ? ` (${pl.free_count})` : ""}
        </button>
      </div>
      <div className="grid grid-cols-3 gap-2 text-center mb-2">
        <div className="rounded-lg py-2" style={{ background: "#0E1119" }}>
          <div className="text-[10px]" style={{ color: "#6B7192" }}>Revenue</div>
          <div className="text-sm font-semibold" style={{ color: "#7FBF98" }}>${(pl.revenue ?? 0).toFixed(2)}</div>
        </div>
        <div className="rounded-lg py-2" style={{ background: "#0E1119" }}>
          <div className="text-[10px]" style={{ color: "#6B7192" }}>Tenant cost</div>
          <div className="text-sm font-semibold" style={{ color: "#E0685E" }}>${(pl.ai_cost ?? 0).toFixed(2)}</div>
        </div>
        <div className="rounded-lg py-2" style={{ background: "#0E1119" }}>
          <div className="text-[10px]" style={{ color: "#6B7192" }}>Net</div>
          <div className="text-sm font-semibold" data-testid={`tenant-net-${t.id}`} style={{ color: net >= 0 ? "#7FBF98" : "#E0685E" }}>{net < 0 ? "−" : ""}${Math.abs(net).toFixed(2)}</div>
        </div>
      </div>
      {pl.free_cost > 0 && <div className="text-[10px] mb-1" style={{ color: "#C9A227" }}>Includes {pl.free_count} free credits issued (~${Number(pl.free_cost).toFixed(2)}) across members.</div>}
      {showFree && (
        <div className="space-y-1" data-testid={`tenant-freecredits-list-${t.id}`}>
          {!free ? <div className="text-[10px]" style={{ color: "#6B7192" }}>Loading…</div>
            : free.awards.length === 0 ? <div className="text-[10px]" style={{ color: "#6B7192" }}>No free credits awarded in this tenant.</div>
            : free.awards.map((a, i) => (
              <div key={i} className="flex items-center justify-between text-[10px] rounded px-2 py-1" style={{ background: "#0E1119" }}>
                <span><span style={{ color: "#C5C9FF" }}>{a.user_name}</span> <span style={{ color: "#C9A227" }}>+{a.amount.toLocaleString()} cr</span></span>
                <span style={{ color: "#6B7192" }}>{(a.created_at || "").slice(0, 16).replace("T", " ")} · ~${a.cost.toFixed(2)}</span>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

function Pricing({ pricing, packs, onSaved }) {
  const [p, setP] = useState(pricing);
  const [ck, setCk] = useState(packs || {});
  const [saving, setSaving] = useState(false);
  const upd = (tier, field, val) => setP((prev) => ({ ...prev, [tier]: { ...prev[tier], [field]: val } }));
  const updPack = (id, field, val) => setCk((prev) => ({ ...prev, [id]: { ...prev[id], [field]: val } }));
  const save = async () => {
    setSaving(true);
    try {
      const payload = { pricing: {}, credit_packs: {} };
      ["roots", "branches", "legacy"].forEach((k) => {
        payload.pricing[k] = { monthly: parseFloat(p[k].monthly) || 0, yearly: parseFloat(p[k].yearly) || 0 };
      });
      Object.keys(ck).forEach((id) => {
        payload.credit_packs[id] = { credits: parseInt(ck[id].credits, 10) || 0, price: parseFloat(ck[id].price) || 0 };
      });
      await api.patch("/superadmin/pricing", payload);
      toast.success("Pricing & credit packs updated app-wide");
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const perCredit = (id) => {
    const cr = parseInt(ck[id]?.credits, 10) || 0;
    const pr = parseFloat(ck[id]?.price) || 0;
    return cr > 0 ? `$${(pr / cr).toFixed(3)}/credit` : "—";
  };
  return (
    <div data-testid="superadmin-pricing">
      <h2 className="text-xl font-semibold mb-1">App-wide Pricing</h2>
      <p className="text-xs mb-4" style={{ color: "#6B7192" }}>Changes apply across the entire app for new subscriptions and credit purchases.</p>
      <div className="space-y-3 max-w-lg">
        {["roots", "branches", "legacy"].map((k) => (
          <div key={k} className="rounded-xl p-4" style={{ background: "#12151F", border: "1px solid #1E2233" }}>
            <div className="font-semibold capitalize mb-3">{k}</div>
            <div className="grid grid-cols-2 gap-3">
              {["monthly", "yearly"].map((f) => (
                <label key={f} className="block">
                  <span className="text-[11px] uppercase tracking-wider" style={{ color: "#6B7192" }}>{f} ($)</span>
                  <input type="number" min="0" step="0.01" value={p[k][f]}
                    data-testid={`price-${k}-${f}`}
                    onChange={(e) => upd(k, f, e.target.value)}
                    className="w-full mt-1 rounded-lg px-3 py-2 text-sm outline-none"
                    style={{ background: "#0E1119", border: "1px solid #1E2233", color: "#E7E4DB" }} />
                </label>
              ))}
            </div>
          </div>
        ))}

        <div className="rounded-xl p-4" style={{ background: "#12151F", border: "1px solid #1E2233" }} data-testid="credit-packs-editor">
          <div className="flex items-center gap-2 mb-1">
            <Zap size={15} className="gold-text" />
            <div className="font-semibold">A-la-carte AI Credit Packs</div>
          </div>
          <p className="text-[11px] mb-3" style={{ color: "#6B7192" }}>
            Change credit quantity or price — it updates instantly in every user's Buy Credits screen, offers and Stripe invoices. Purchased credits never expire.
          </p>
          <div className="space-y-3">
            {Object.keys(ck).map((id) => (
              <div key={id} className="rounded-lg p-3" style={{ background: "#0E1119", border: "1px solid #1E2233" }}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] uppercase tracking-wider" style={{ color: "#6B7192" }}>Pack</span>
                  <span className="text-[10px] gold-text">{perCredit(id)}</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="text-[11px]" style={{ color: "#6B7192" }}>Credits (quantity)</span>
                    <input type="number" min="1" value={ck[id].credits}
                      data-testid={`pack-${id}-credits`}
                      onChange={(e) => updPack(id, "credits", e.target.value)}
                      className="w-full mt-1 rounded-lg px-3 py-2 text-sm outline-none"
                      style={{ background: "#12151F", border: "1px solid #1E2233", color: "#E7E4DB" }} />
                  </label>
                  <label className="block">
                    <span className="text-[11px]" style={{ color: "#6B7192" }}>Price ($)</span>
                    <input type="number" min="0" step="0.01" value={ck[id].price}
                      data-testid={`pack-${id}-price`}
                      onChange={(e) => updPack(id, "price", e.target.value)}
                      className="w-full mt-1 rounded-lg px-3 py-2 text-sm outline-none"
                      style={{ background: "#12151F", border: "1px solid #1E2233", color: "#E7E4DB" }} />
                  </label>
                </div>
              </div>
            ))}
          </div>
        </div>

        <button onClick={save} disabled={saving} data-testid="pricing-save-btn"
          className="w-full rounded-xl py-3 font-semibold text-sm flex items-center justify-center gap-2"
          style={{ background: "#7C83FF", color: "#fff" }}>
          {saving && <Loader2 size={16} className="animate-spin" />} Save Pricing & Packs
        </button>
      </div>
    </div>
  );
}

// ---------------- Super AI evidence side-panel ----------------
function parseParamString(s) {
  const params = {};
  (s || "").split(";").forEach((pair) => {
    const idx = pair.indexOf("=");
    if (idx === -1) return;
    const k = pair.slice(0, idx).trim().toLowerCase();
    let v = pair.slice(idx + 1).trim();
    try { v = decodeURIComponent(v.replace(/\+/g, " ")); } catch (e) { /* keep raw */ }
    if (k && v) params[k] = v;
  });
  return params;
}

function parseArtifact(text) {
  const m = (text || "").match(/\[SHOW_ARTIFACT:\s*([^\]]+)\]/i);
  if (!m) return { text: text || "", artifact: null };
  const params = parseParamString(m[1]);
  const clean = (text || "").replace(m[0], "").trim();
  return { text: clean, artifact: params.type ? params : null };
}

const HIDE_KEYS = new Set(["_id", "id", "password", "password_hash", "secret_vault", "secret_vault_items", "turns"]);

function renderVal(v) {
  if (v == null) return "";
  if (typeof v === "object") return <pre className="whitespace-pre-wrap m-0" style={{ fontFamily: "inherit" }}>{JSON.stringify(v, null, 2)}</pre>;
  const s = String(v);
  return s.length > 600 ? s.slice(0, 600) + "…" : s;
}

function RecordCard({ rec }) {
  if (rec == null || typeof rec !== "object") return <div className="text-xs" style={{ color: "#D8D5CC" }}>{String(rec)}</div>;
  const entries = Object.entries(rec).filter(([k, v]) => !HIDE_KEYS.has(k) && v !== null && v !== "" && !(Array.isArray(v) && v.length === 0));
  return (
    <div className="rounded-xl p-3 text-xs" style={{ background: "#12151F", border: "1px solid #1E2233" }} data-testid="artifact-record">
      {entries.map(([k, v]) => (
        <div key={k} className="flex gap-2 py-0.5">
          <span className="shrink-0 uppercase tracking-wide" style={{ color: "#6B7192", minWidth: 92 }}>{k.replace(/_/g, " ")}</span>
          <span className="min-w-0 break-words" style={{ color: "#D8D5CC" }}>{renderVal(v)}</span>
        </div>
      ))}
    </div>
  );
}

function MetaRow({ meta }) {
  if (!meta) return null;
  const entries = Object.entries(meta).filter(([, v]) => v != null && v !== "");
  if (!entries.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {entries.map(([k, v]) => (
        <span key={k} className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: "#141824", border: "1px solid #1E2233", color: "#8B90AE" }}>
          {k}: {String(v)}
        </span>
      ))}
    </div>
  );
}

const SectionLabel = ({ children }) => (
  <div className="text-[11px] uppercase tracking-wider pt-1" style={{ color: "#6B7192" }}>{children}</div>
);

function ArtifactContent({ art }) {
  if (art.loading) return <div className="grid place-items-center py-20"><Loader2 className="animate-spin" style={{ color: "#7C83FF" }} /></div>;
  if (art.error) return <div className="text-sm rounded-lg p-3" style={{ background: "#1A1220", color: "#E0685E", border: "1px solid #3A2230" }} data-testid="artifact-error">{art.error}</div>;
  const d = art.data;
  if (!d) return null;

  if (d.type === "ai_thread") {
    const turns = d.turns || [];
    return (
      <div className="space-y-2">
        <MetaRow meta={d.meta} />
        {turns.length === 0 && <div className="text-sm" style={{ color: "#6B7192" }}>Empty conversation.</div>}
        {turns.map((t, i) => (
          <div key={i} className={`flex ${t.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className="rounded-2xl px-3 py-2 text-xs max-w-[88%] whitespace-pre-wrap"
              style={t.role === "user" ? { background: "#7C83FF", color: "#fff" } : { background: "#12151F", border: "1px solid #1E2233", color: "#D8D5CC" }}>
              {t.text}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (d.type === "session") {
    const sess = d.sessions || [];
    const logins = d.logins || [];
    return (
      <div className="space-y-3">
        <MetaRow meta={d.meta} />
        <SectionLabel>Sessions ({sess.length})</SectionLabel>
        {sess.length === 0 && <div className="text-xs" style={{ color: "#6B7192" }}>No sessions.</div>}
        {sess.map((s, i) => <RecordCard key={"s" + i} rec={s} />)}
        <SectionLabel>Login attempts ({logins.length})</SectionLabel>
        {logins.length === 0 && <div className="text-xs" style={{ color: "#6B7192" }}>No login records.</div>}
        {logins.map((l, i) => <RecordCard key={"l" + i} rec={l} />)}
      </div>
    );
  }

  const items = d.messages || d.items || (d.data ? [d.data] : []);
  return (
    <div className="space-y-3">
      <MetaRow meta={d.meta} />
      {items.length === 0 && <div className="text-sm" style={{ color: "#6B7192" }}>No records found for this request.</div>}
      {items.map((it, i) => <RecordCard key={i} rec={it} />)}
    </div>
  );
}

function ArtifactPanel({ art, onClose, mobile }) {
  const d = art.data;
  return (
    <div className="flex flex-col flex-1 min-h-0 rounded-2xl overflow-hidden" data-testid="artifact-panel"
      style={{ background: "#0B0E16", border: mobile ? "none" : "1px solid #1E2233" }}>
      {mobile && <div className="mx-auto h-1.5 w-12 rounded-full mt-2 mb-1" style={{ background: "#2A2F45" }} />}
      <div className="flex items-center justify-between px-4 py-3 shrink-0" style={{ borderBottom: "1px solid #1E2233" }}>
        <div className="flex items-center gap-2 min-w-0">
          <FileText size={16} style={{ color: "#7C83FF" }} />
          <span className="text-sm font-semibold truncate" style={{ color: "#E7E4DB" }}>{(d && d.title) || "Evidence"}</span>
        </div>
        <button onClick={onClose} data-testid="artifact-close" className="p-1.5 rounded-lg hover:bg-white/5 transition-colors">
          <X size={16} style={{ color: "#6B7192" }} />
        </button>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto p-4">
        <ArtifactContent art={art} />
      </div>
    </div>
  );
}

function SuperAI({ tenants }) {
  const [scope, setScope] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [art, setArt] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const endRef = useRef();
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const msgRefs = useRef({});
  const scrollHint = useRef("bottom");
  useAutoGrow(inputRef, input, 4);

  useEffect(() => {
    const c = containerRef.current;
    if (!c) return;
    if (scrollHint.current === "start") {
      let lastUser = -1;
      messages.forEach((m, i) => { if (m.role === "user") lastUser = i; });
      const el = msgRefs.current[lastUser];
      if (el) { c.scrollTop = Math.max(0, el.offsetTop - c.offsetTop - 8); return; }
    }
    c.scrollTop = c.scrollHeight;
  }, [messages, busy]);

  const loadArtifact = async (params) => {
    setArt({ params, loading: true });
    try {
      const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v != null && v !== "")).toString();
      const { data } = await api.get(`/superadmin/artifact?${qs}`);
      setArt({ params, loading: false, data });
    } catch (e) {
      setArt({ params, loading: false, error: formatApiError(e.response?.data?.detail) || "Could not load this evidence." });
    }
  };
  const openArtifact = (params) => { setMobileOpen(true); loadArtifact(params); };

  const mdComponents = {
    a: ({ href, children }) => {
      if (href && href.toLowerCase().startsWith("artifact:")) {
        const params = parseParamString(href.slice("artifact:".length));
        if (params.type) {
          return (
            <button type="button" onClick={() => openArtifact(params)} data-testid="artifact-inline-link"
              className="inline items-center underline decoration-dotted underline-offset-2 font-medium transition-colors hover:decoration-solid"
              style={{ color: "#9AA6FF", cursor: "pointer", background: "none", padding: 0 }}>
              {children}
            </button>
          );
        }
      }
      return <a href={href} target="_blank" rel="noreferrer" style={{ color: "#9AA6FF" }}>{children}</a>;
    },
  };

  const send = async () => {
    if (!input.trim() || busy) return;
    const q = input.trim();
    scrollHint.current = "bottom";
    setMessages((m) => [...m, { role: "user", text: q }]);
    setInput(""); setBusy(true);
    try {
      const { data } = await api.post("/superadmin/ai", { query: q, tenant_id: scope || null });
      const parsed = parseArtifact(data.reply);
      scrollHint.current = "start";
      setMessages((m) => [...m, { role: "ai", text: parsed.text, artifact: parsed.artifact, scope: data.tenants_in_scope }]);
      if (parsed.artifact) loadArtifact(parsed.artifact);
    } catch (e) {
      scrollHint.current = "start";
      setMessages((m) => [...m, { role: "ai", text: formatApiError(e.response?.data?.detail) || "Error" }]);
    } finally { setBusy(false); }
  };

  return (
    <div className="flex gap-4" style={{ height: "calc(100vh - 130px)" }} data-testid="superadmin-superai">
      <div className="flex flex-col flex-1 min-w-0">
        <h2 className="text-xl font-semibold mb-1">Super AI</h2>
        <p className="text-xs mb-3" style={{ color: "#6B7192" }}>Ask anything across your tenants. Data is strictly scoped — never mixed across families. Ask to <em>see</em> a chat, post or activity trail to pull it up as evidence.</p>
        <select value={scope} onChange={(e) => setScope(e.target.value)} data-testid="superai-scope"
          className="rounded-lg px-3 py-2 text-sm mb-3 outline-none max-w-xs" style={{ background: "#12151F", border: "1px solid #1E2233", color: "#E7E4DB" }}>
          <option value="">All tenants</option>
          {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>

        <div ref={containerRef} className="flex-1 min-h-0 overflow-y-auto space-y-3 rounded-xl p-4" style={{ background: "#0E1119", border: "1px solid #1E2233" }}>
          {messages.length === 0 && <div className="text-sm" style={{ color: "#6B7192" }}>e.g. "Which family has the most reunions?" or "Show me John's AI chats" or "Pull up demo's activity trail".</div>}
          {messages.map((m, i) => (
            <div key={i} ref={(el) => { msgRefs.current[i] = el; }} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`superai-msg-${m.role}`}>
              <div className="rounded-2xl px-4 py-2.5 text-sm max-w-[85%]"
                style={m.role === "user" ? { background: "#7C83FF", color: "#fff" } : { background: "#12151F", border: "1px solid #1E2233" }}>
                {m.role === "user" ? (
                  <span className="whitespace-pre-wrap">{m.text}</span>
                ) : (
                  <>
                    <div className="superai-md"><ReactMarkdown remarkPlugins={[remarkGfm]} urlTransform={(u) => u} components={mdComponents}>{m.text}</ReactMarkdown></div>
                    {m.artifact && (
                      <button onClick={() => openArtifact(m.artifact)} data-testid="artifact-view-chip"
                        className="mt-2 inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full transition-colors hover:bg-white/5"
                        style={{ background: "#141824", border: "1px solid #2A2F45", color: "#9AA0C0" }}>
                        <Eye size={13} style={{ color: "#7C83FF" }} /> View evidence
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
          {busy && <div className="flex justify-start"><div className="rounded-2xl px-4 py-2.5" style={{ background: "#12151F" }}><Loader2 size={16} className="animate-spin" style={{ color: "#7C83FF" }} /></div></div>}
          <div ref={endRef} />
        </div>

        <div className="flex items-end gap-2 mt-3">
          <textarea ref={inputRef} value={input} rows={1} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Ask the Super AI…" data-testid="superai-input"
            className="flex-1 rounded-xl px-4 py-3 text-sm outline-none resize-none" style={{ background: "#12151F", border: "1px solid #1E2233", color: "#E7E4DB", maxHeight: "6.5rem" }} />
          <button onClick={send} disabled={busy} data-testid="superai-send"
            className="h-11 w-11 rounded-xl grid place-items-center" style={{ background: "#7C83FF", color: "#fff" }}>
            <Send size={18} />
          </button>
        </div>
      </div>

      {/* Desktop split-screen evidence panel */}
      <AnimatePresence>
        {art && (
          <motion.div className="hidden lg:flex flex-col shrink-0" style={{ width: "42%", maxWidth: 640 }}
            initial={{ opacity: 0, x: 28 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 28 }}
            transition={{ type: "spring", damping: 28, stiffness: 260 }}>
            <ArtifactPanel art={art} onClose={() => setArt(null)} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile bottom-sheet evidence popup */}
      <AnimatePresence>
        {mobileOpen && art && (
          <>
            <motion.div className="fixed inset-0 z-[60] lg:hidden" style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setMobileOpen(false)} data-testid="artifact-backdrop" />
            <motion.div className="fixed left-0 right-0 bottom-0 z-[70] lg:hidden flex flex-col rounded-t-3xl"
              style={{ height: "88dvh", background: "#0B0E16", border: "1px solid #1E2233" }}
              initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 30, stiffness: 300 }}>
              <ArtifactPanel art={art} onClose={() => setMobileOpen(false)} mobile />
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}


const KIND_LABEL = { subscription: "Subscription", reunion_fee: "Reunion Fee", dues: "Dues",
  credit_pack: "Credit Pack", free_credits: "Free Credits (loss)" };
const TOOL_LABEL = {
  heritage_navigator: "Heritage Navigator", event_planner: "Event Planner", game_generator: "Game Generator",
  historian: "Historian", kinworks: "KinWorks", mentor_matchmaker: "Mentor Matchmaker",
  respawner: "Respawner", legacy_suite: "Legacy Suite", time_capsule: "Time Capsule", secret_vault: "Secret Vault",
};

function Revenue() {
  const [data, setData] = useState(null);
  const load = async () => { try { const res = await api.get("/superadmin/revenue"); setData(res.data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  useEffect(() => { load(); }, []);
  const refund = async (id) => {
    try { await api.post("/superadmin/refund", { event_id: id }); toast.success("Refunded"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!data) return <div className="grid place-items-center py-16"><Loader2 className="animate-spin" style={{ color: "#7C83FF" }} /></div>;
  const t = data.totals;
  const losses = data.losses || { free_credits: 0, testing_credits: 0 };
  const td = data.testing_detail || { credits: 0, by_tool: {}, accounts: 0 };
  return (
    <div data-testid="superadmin-revenue">
      <h2 className="text-xl font-semibold mb-4">Platform Revenue</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Stat icon={Receipt} label="Net Total" value={`$${t.platform_total.toFixed(2)}`} sub="revenue minus credit losses" />
        <Stat icon={Sparkles} label="Subscriptions" value={`$${(t.subscription || 0).toFixed(2)}`} />
        <Stat icon={Zap} label="Credit Packs" value={`$${(t.credit_pack || 0).toFixed(2)}`} sub="add-on AI credits sold" />
        <Stat icon={CalendarClock} label="Reunion Fees" value={`$${(t.reunion_fee || 0).toFixed(2)}`} />
      </div>
      <div className="text-[11px] uppercase tracking-wider mb-2 flex items-center gap-1.5" style={{ color: "#6B7192" }}>
        <TrendingDown size={13} style={{ color: "#E0685E" }} /> Credit losses
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
        <LossStat label="Free Credits" value={losses.free_credits} sub="issued to users by admin" />
        <LossStat label="Testing Credits" value={losses.testing_credits}
          sub={`${td.credits} cr across ${td.accounts} demo account${td.accounts === 1 ? "" : "s"}`} testid="testing" />
        <Stat icon={DollarSign} label="Dues (our cut)" value={`$${(t.dues || 0).toFixed(2)}`} sub="5% less Stripe fees" />
      </div>
      {Object.keys(td.by_tool).length > 0 && (
        <div className="rounded-xl p-3 mb-5" style={{ background: "#12151F", border: "1px solid #1E2233" }} data-testid="testing-credits-breakdown">
          <div className="text-[11px] uppercase tracking-wider mb-2" style={{ color: "#6B7192" }}>Testing credits — what they were spent on</div>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(td.by_tool).sort((a, b) => b[1] - a[1]).map(([tool, v]) => (
              <span key={tool} className="text-[10px] rounded-full px-2 py-0.5" style={{ background: "#0E1119", color: "#9DA4FF" }}>
                {TOOL_LABEL[tool] || tool}: {v} cr
              </span>
            ))}
          </div>
        </div>
      )}
      <div className="rounded-xl overflow-hidden" style={{ background: "#12151F", border: "1px solid #1E2233" }}>
        <div className="grid grid-cols-12 px-4 py-2 text-[11px] uppercase tracking-wider" style={{ color: "#6B7192", background: "#0E1119" }}>
          <div className="col-span-3">Source</div><div className="col-span-3">Family / User</div>
          <div className="col-span-2 text-right">Charged</div><div className="col-span-2 text-right">Our revenue</div><div className="col-span-2 text-right">Action</div>
        </div>
        {data.events.length === 0 && <div className="px-4 py-8 text-center text-sm" style={{ color: "#6B7192" }}>No transactions yet.</div>}
        {data.events.map((e) => {
          const isLoss = Number(e.platform_revenue) < 0;
          return (
          <div key={e.id} className="grid grid-cols-12 px-4 py-2.5 text-xs items-center border-t" style={{ borderColor: "#1E2233", opacity: e.refunded ? 0.5 : 1 }} data-testid={`revenue-row-${e.id}`}>
            <div className="col-span-3">{KIND_LABEL[e.kind] || e.kind}{e.kind === "dues" && e.meta?.dues_amount ? ` ($${Number(e.meta.dues_amount).toFixed(0)})` : ""}{e.kind === "free_credits" && e.meta?.credits ? ` (${e.meta.credits} cr)` : ""}</div>
            <div className="col-span-3 truncate" style={{ color: "#8890B0" }}>{e.family || "—"}<br /><span className="text-[10px]">{e.user}</span></div>
            <div className="col-span-2 text-right">${Number(e.amount).toFixed(2)}</div>
            <div className="col-span-2 text-right" style={{ color: isLoss ? "#E0685E" : "#9DA4FF" }}>{isLoss ? "−" : ""}${Math.abs(Number(e.platform_revenue)).toFixed(2)}</div>
            <div className="col-span-2 text-right">
              {e.kind === "free_credits" ? <span className="text-[10px]" style={{ color: "#6B7192" }}>—</span>
                : e.refunded ? <span className="text-[10px]" style={{ color: "#E0685E" }}>refunded</span> : (
                <button onClick={() => refund(e.id)} data-testid={`refund-btn-${e.id}`} className="inline-flex items-center gap-1 text-[11px] rounded-lg px-2 py-1" style={{ background: "#E0685E22", color: "#E0685E" }}>
                  <RotateCcw size={11} /> Refund
                </button>
              )}
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
}

function LossStat({ label, value, sub, testid }) {
  return (
    <div className="rounded-xl p-4" style={{ background: "#12151F", border: "1px solid #E0685E33" }} data-testid={`loss-${testid || label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider" style={{ color: "#6B7192" }}>
        <TrendingDown size={14} style={{ color: "#E0685E" }} /> {label}
      </div>
      <div className="text-2xl font-bold mt-2" style={{ color: "#E0685E" }}>−${Number(value || 0).toFixed(2)}</div>
      {sub && <div className="text-[11px] mt-1" style={{ color: "#6B7192" }}>{sub}</div>}
    </div>
  );
}

function Support() {
  const [threads, setThreads] = useState([]);
  const [view, setView] = useState("active"); // active | archived
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [sending, setSending] = useState(false);
  const [closing, setClosing] = useState(false);
  const endRef = useRef();

  const load = async (v = view) => {
    try {
      const res = await api.get(v === "archived" ? "/superadmin/support/archived" : "/superadmin/support");
      setThreads(res.data);
      if (active) { const up = res.data.find((t) => t.user_id === active.user_id); if (up) setActive(up); }
    } catch {}
  };
  useEffect(() => { load(view); setActive(null); }, [view]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [active]);

  const search = async (val) => {
    setQ(val);
    if (!val.trim()) { setResults([]); return; }
    try { const { data } = await api.get(`/superadmin/users/search?q=${encodeURIComponent(val)}`); setResults(data); } catch {}
  };

  const send = async () => {
    if (!reply.trim() || !active) return;
    setSending(true);
    try { await api.post("/superadmin/support/send", { user_id: active.user_id, text: reply.trim() }); setReply(""); await load(); toast.success("Message sent"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const closeThread = async () => {
    if (!active) return;
    setClosing(true);
    try {
      await api.post("/superadmin/support/close", { user_id: active.user_id });
      toast.success("Support ticket closed & archived");
      setActive(null);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setClosing(false); }
  };

  const openUser = (u) => { setActive({ user_id: u.id, user_name: u.name, user_email: u.email, messages: [] }); setResults([]); setQ(""); load(); };

  return (
    <div data-testid="superadmin-support">
      <h2 className="text-xl font-semibold mb-1">Support</h2>
      <p className="text-xs mb-3" style={{ color: "#6B7192" }}>Reply to user messages or search any user to reach out. Replies land in their inbox as "Vault Support". Close a ticket to archive it and remove it from the user's inbox.</p>
      <div className="flex gap-1 mb-4 p-1 rounded-xl w-fit" style={{ background: "#12151F", border: "1px solid #1E2233" }}>
        {[["active", "Open"], ["archived", "Archived"]].map(([k, lbl]) => (
          <button key={k} onClick={() => setView(k)} data-testid={`support-view-${k}`}
            className="rounded-lg px-4 py-1.5 text-xs font-semibold"
            style={view === k ? { background: "#7C83FF", color: "#fff" } : { color: "#8890B0" }}>
            {lbl}
          </button>
        ))}
      </div>
      <div className="md:grid md:grid-cols-3 md:gap-4">
        {/* thread list + search */}
        <div className={active ? "hidden md:block" : "block"}>
          {view === "active" && (
            <div className="relative mb-3">
              <Search size={14} className="absolute left-3 top-3" style={{ color: "#6B7192" }} />
              <input value={q} onChange={(e) => search(e.target.value)} placeholder="Search any user…" data-testid="support-user-search"
                className="w-full rounded-lg pl-9 pr-3 py-2.5 text-sm outline-none" style={{ background: "#12151F", border: "1px solid #1E2233", color: "#E7E4DB" }} />
            </div>
          )}
          {results.map((u) => (
            <button key={u.id} onClick={() => openUser(u)} data-testid={`support-search-result-${u.id}`}
              className="w-full text-left rounded-lg px-3 py-2 mb-1 text-sm" style={{ background: "#12151F", border: "1px solid #1E2233" }}>
              {u.name} <span className="text-[11px]" style={{ color: "#6B7192" }}>· {u.email}</span>
            </button>
          ))}
          {!q && (
            <div className="space-y-1">
              {threads.length === 0 && <div className="text-sm" style={{ color: "#6B7192" }}>{view === "archived" ? "No archived tickets." : "No support messages yet."}</div>}
              {threads.map((t) => (
                <button key={t.user_id} onClick={() => setActive(t)} data-testid={`support-thread-${t.user_id}`}
                  className="w-full text-left rounded-lg px-3 py-2.5 text-sm" style={{ background: active?.user_id === t.user_id ? "#7C83FF22" : "#12151F", border: "1px solid #1E2233" }}>
                  <div className="font-medium">{t.user_name}</div>
                  <div className="text-[11px] truncate" style={{ color: "#6B7192" }}>{t.messages[t.messages.length - 1]?.text}</div>
                </button>
              ))}
            </div>
          )}
        </div>
        {/* conversation */}
        <div className="md:col-span-2 mt-4 md:mt-0">
          {!active ? (
            <div className="hidden md:grid place-items-center h-64 text-sm" style={{ color: "#6B7192" }}>Select a conversation{view === "active" ? " or search a user" : ""}.</div>
          ) : (
            <div className="rounded-xl p-3 flex flex-col" style={{ background: "#0E1119", border: "1px solid #1E2233", height: "60vh" }} data-testid="support-conversation">
              <div className="flex items-center gap-2 pb-2 mb-2 border-b" style={{ borderColor: "#1E2233" }}>
                <button className="md:hidden" onClick={() => setActive(null)}><ChevronRight size={16} className="rotate-180" /></button>
                <div className="flex-1"><div className="font-medium text-sm">{active.user_name}</div><div className="text-[11px]" style={{ color: "#6B7192" }}>{active.user_email}</div></div>
                {view === "active" && active.messages.length > 0 && (
                  <button onClick={closeThread} disabled={closing} data-testid="support-close-btn"
                    className="flex items-center gap-1.5 text-xs rounded-lg px-3 py-1.5" style={{ background: "#E0685E22", color: "#E0685E" }}>
                    {closing ? <Loader2 size={13} className="animate-spin" /> : <Archive size={13} />} Close ticket
                  </button>
                )}
                {view === "archived" && (
                  <span className="flex items-center gap-1.5 text-[11px] rounded-lg px-2.5 py-1" style={{ background: "#1E2233", color: "#8890B0" }}><Archive size={12} /> Archived</span>
                )}
              </div>
              <div className="flex-1 overflow-y-auto space-y-2">
                {active.messages.map((m) => (
                  <div key={m.id} className={`flex ${m.direction === "from_support" ? "justify-end" : "justify-start"}`}>
                    <div className="rounded-2xl px-3 py-2 text-sm max-w-[80%] whitespace-pre-wrap" style={m.direction === "from_support" ? { background: "#7C83FF", color: "#fff" } : { background: "#12151F", border: "1px solid #1E2233" }}>{m.text}</div>
                  </div>
                ))}
                <div ref={endRef} />
              </div>
              {view === "active" && (
                <div className="flex items-center gap-2 mt-2">
                  <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={2} placeholder="Reply as Vault Support… (click send)"
                    data-testid="support-reply-input" className="flex-1 rounded-xl px-3 py-2.5 text-sm outline-none resize-none" style={{ background: "#12151F", border: "1px solid #1E2233", color: "#E7E4DB" }} />
                  <button onClick={send} disabled={sending} data-testid="support-reply-send" className="h-10 w-10 rounded-xl grid place-items-center" style={{ background: "#7C83FF", color: "#fff" }}>
                    {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ---------------- Promotions (referral campaign) ----------------
const PROMO_META = [
  { key: "join_family", label: "Friend joins your family", hint: "Credited when an invited friend joins the referrer's family." },
  { key: "create_family", label: "Friend starts their own family", hint: "Credited when an invited friend creates a new family." },
  { key: "upgrade", label: "Friend upgrades to a paid plan", hint: "Credited when an invited friend upgrades within the window below." },
];

function Toggle({ on, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="relative h-6 w-11 rounded-full transition-colors shrink-0"
      style={{ background: on ? "#7C83FF" : "#2A2F45" }}>
      <span className="absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all" style={{ left: on ? 22 : 2 }} />
    </button>
  );
}

function Promotions() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/superadmin/promotions"); setCfg(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  if (!cfg) return <div className="grid place-items-center py-24"><Loader2 className="animate-spin" style={{ color: "#7C83FF" }} /></div>;

  const setPromo = (key, patch) =>
    setCfg((c) => ({ ...c, promos: { ...c.promos, [key]: { ...c.promos[key], ...patch } } }));

  const toggleTenant = (id) =>
    setCfg((c) => {
      const ids = new Set(c.tenant_ids || []);
      ids.has(id) ? ids.delete(id) : ids.add(id);
      return { ...c, tenant_ids: [...ids] };
    });

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        active: !!cfg.active,
        expiry_days: Number(cfg.expiry_days) || 30,
        duration_days: Number(cfg.duration_days) || 30,
        scope: cfg.scope === "tenants" ? "tenants" : "global",
        tenant_ids: cfg.scope === "tenants" ? (cfg.tenant_ids || []) : [],
        promos: {
          join_family: { credits: Number(cfg.promos.join_family.credits) || 0, active: !!cfg.promos.join_family.active },
          create_family: { credits: Number(cfg.promos.create_family.credits) || 0, active: !!cfg.promos.create_family.active },
          upgrade: {
            credits: Number(cfg.promos.upgrade.credits) || 0, active: !!cfg.promos.upgrade.active,
            window_days: Number(cfg.promos.upgrade.window_days) || 10,
          },
        },
      };
      const { data } = await api.put("/superadmin/promotions", body);
      setCfg(data);
      toast.success("Promotions updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const numInput = (val, onChange, testid) => (
    <input type="number" min={0} value={val} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-24 rounded-lg px-3 py-2 text-sm outline-none"
      style={{ background: "#0E1119", border: "1px solid #262B3D", color: "#E7EAF5" }} />
  );

  return (
    <div data-testid="superadmin-promotions">
      <div className="flex items-start justify-between gap-4 mb-1">
        <div>
          <h2 className="text-xl font-semibold mb-1">Promotions</h2>
          <p className="text-xs" style={{ color: "#6B7192" }}>
            Referral rewards paid in AI credits to the <b>referrer</b>. When the campaign is off, the lightning invite button is hidden for everyone.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs" style={{ color: cfg.active ? "#9DE7B0" : "#6B7192" }}>{cfg.active ? "Live" : "Off"}</span>
          <Toggle on={cfg.active} onClick={() => setCfg((c) => ({ ...c, active: !c.active }))} testid="promo-master-toggle" />
        </div>
      </div>

      {/* Reward amounts */}
      <div className="mt-5 space-y-3">
        {PROMO_META.map((m) => {
          const p = cfg.promos[m.key] || {};
          return (
            <div key={m.key} className="rounded-xl p-4" style={{ background: "#12151F", border: "1px solid #1E2233" }} data-testid={`promo-card-${m.key}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold">{m.label}</div>
                  <div className="text-[11px] mt-0.5" style={{ color: "#6B7192" }}>{m.hint}</div>
                </div>
                <Toggle on={!!p.active} onClick={() => setPromo(m.key, { active: !p.active })} testid={`promo-toggle-${m.key}`} />
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-4">
                <label className="flex items-center gap-2 text-xs" style={{ color: "#8890B0" }}>
                  Credits {numInput(p.credits ?? 0, (v) => setPromo(m.key, { credits: v }), `promo-credits-${m.key}`)}
                </label>
                {m.key === "upgrade" && (
                  <label className="flex items-center gap-2 text-xs" style={{ color: "#8890B0" }}>
                    Within (days) {numInput(p.window_days ?? 10, (v) => setPromo(m.key, { window_days: v }), "promo-window-days")}
                  </label>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Duration + expiry */}
      <div className="mt-4 rounded-xl p-4 grid sm:grid-cols-2 gap-4" style={{ background: "#12151F", border: "1px solid #1E2233" }}>
        <label className="text-xs" style={{ color: "#8890B0" }}>
          <div className="mb-1.5">Campaign runs for (days from activation)</div>
          {numInput(cfg.duration_days ?? 30, (v) => setCfg((c) => ({ ...c, duration_days: v })), "promo-duration-days")}
        </label>
        <label className="text-xs" style={{ color: "#8890B0" }}>
          <div className="mb-1.5">Issued credits expire after (days)</div>
          {numInput(cfg.expiry_days ?? 30, (v) => setCfg((c) => ({ ...c, expiry_days: v })), "promo-expiry-days")}
        </label>
      </div>

      {/* Targeting */}
      <div className="mt-4 rounded-xl p-4" style={{ background: "#12151F", border: "1px solid #1E2233" }}>
        <div className="text-sm font-semibold mb-2">Who sees this promotion?</div>
        <div className="flex gap-2 mb-3">
          {[["global", "All tenants"], ["tenants", "Specific tenants"]].map(([v, l]) => (
            <button key={v} onClick={() => setCfg((c) => ({ ...c, scope: v }))} data-testid={`promo-scope-${v}`}
              className="rounded-lg px-3 py-2 text-xs"
              style={cfg.scope === v ? { background: "#7C83FF22", color: "#C5C9FF", border: "1px solid #7C83FF55" } : { background: "#0E1119", color: "#8890B0", border: "1px solid #262B3D" }}>
              {l}
            </button>
          ))}
        </div>
        {cfg.scope === "tenants" && (
          <div className="grid sm:grid-cols-2 gap-2" data-testid="promo-tenant-list">
            {(cfg.all_tenants || []).map((t) => {
              const on = (cfg.tenant_ids || []).includes(t.id);
              return (
                <button key={t.id} onClick={() => toggleTenant(t.id)} data-testid={`promo-tenant-${t.id}`}
                  className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm text-left"
                  style={on ? { background: "#7C83FF22", color: "#E7EAF5", border: "1px solid #7C83FF55" } : { background: "#0E1119", color: "#8890B0", border: "1px solid #262B3D" }}>
                  <span className="h-4 w-4 rounded grid place-items-center shrink-0" style={{ background: on ? "#7C83FF" : "transparent", border: on ? "none" : "1px solid #3A4058" }}>
                    {on && <Check size={12} color="#fff" />}
                  </span>
                  <span className="truncate">{t.name}</span>
                </button>
              );
            })}
            {(cfg.all_tenants || []).length === 0 && <div className="text-xs" style={{ color: "#6B7192" }}>No tenants yet.</div>}
          </div>
        )}
      </div>

      <button onClick={save} disabled={saving} data-testid="promo-save-btn"
        className="mt-5 flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold"
        style={{ background: "#7C83FF", color: "#0B0D14" }}>
        {saving && <Loader2 size={16} className="animate-spin" />} Save Promotions
      </button>
    </div>
  );
}
